import React, { useEffect, useMemo, useRef } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/shared/utils/cn";
import {
  buildPeriodChips,
  buildPeriodKey,
  getPeriodRange,
  type Granularity,
  type PeriodRange,
} from "../utils/scheduledShiftRange";

const GRANULARITY_OPTIONS: { value: Granularity; label: string; short: string }[] = [
  { value: "day", label: "Day", short: "D" },
  { value: "week", label: "Week", short: "W" },
  { value: "month", label: "Month", short: "M" },
];

interface DateRangeSelectorProps {
  /** Currently selected range. */
  value: PeriodRange;
  /** Called when the user picks a new period (chip click) or changes granularity. */
  onChange: (range: PeriodRange) => void;
  /** How many chips to render before/after the selected one. */
  chipsBefore?: number;
  chipsAfter?: number;
  className?: string;
}

/**
 * Granularity dropdown (D/W/M) + horizontally scrollable period chips matching
 * the scheduled-shifts screenshot. Selecting a granularity keeps the same
 * anchor date so the user doesn't lose their place.
 */
export function DateRangeSelector({
  value,
  onChange,
  chipsBefore = 2,
  chipsAfter = 2,
  className,
}: DateRangeSelectorProps) {
  const chips = useMemo(
    () => buildPeriodChips(value, { before: chipsBefore, after: chipsAfter }),
    [value, chipsBefore, chipsAfter]
  );
  const selectedKey = buildPeriodKey(value);

  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const selectedChipRef = useRef<HTMLButtonElement | null>(null);

  // Center the selected chip inside its own scroller when the selection
  // changes. We avoid `scrollIntoView` because it walks up the ancestor
  // chain and scrolls the page/IonContent horizontally too, which pushes
  // the whole layout off-screen on narrow viewports.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const node = selectedChipRef.current;
    if (!scroller || !node) return;
    const scrollerRect = scroller.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    const nodeCenter = nodeRect.left + nodeRect.width / 2;
    const scrollerCenter = scrollerRect.left + scrollerRect.width / 2;
    const targetLeft = scroller.scrollLeft + (nodeCenter - scrollerCenter);
    if (typeof scroller.scrollTo === "function") {
      scroller.scrollTo({ left: targetLeft, behavior: "smooth" });
    } else {
      scroller.scrollLeft = targetLeft;
    }
  }, [selectedKey]);

  const handleGranularityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextGranularity = event.target.value as Granularity;
    if (nextGranularity === value.granularity) return;
    // Snap to the period that contains the current start date at the new
    // granularity so the user stays close to where they were.
    onChange(getPeriodRange(value.startDate, nextGranularity));
  };

  return (
    <div
      className={cn(
        // `min-w-0` lets the flex child below actually shrink; without it
        // `flex-1` keeps its content width and pushes the layout off-screen.
        "flex items-center gap-2 w-full min-w-0",
        className
      )}
      data-testid="scheduled-shifts-date-range"
    >
      <div className="relative flex-shrink-0">
        <select
          aria-label="Date range granularity"
          data-testid="scheduled-shifts-granularity"
          value={value.granularity}
          onChange={handleGranularityChange}
          className="appearance-none bg-white border border-gray-200 rounded-full pl-3 pr-7 py-1.5 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {GRANULARITY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.short}
            </option>
          ))}
        </select>
        <ChevronDown
          className="h-3.5 w-3.5 text-gray-500 pointer-events-none absolute right-2 top-1/2 -translate-y-1/2"
          aria-hidden="true"
        />
      </div>

      <div
        ref={scrollerRef}
        className="flex-1 min-w-0 flex items-center gap-2 overflow-x-auto"
        role="tablist"
        aria-label="Scheduled shift periods"
      >
        {chips.map((chip) => {
          const isSelected = chip.key === selectedKey;
          return (
            <button
              key={chip.key}
              ref={isSelected ? selectedChipRef : undefined}
              type="button"
              role="tab"
              aria-selected={isSelected}
              data-testid={`scheduled-shifts-chip-${chip.key}`}
              onClick={() => onChange(chip)}
              className={cn(
                "whitespace-nowrap px-3 py-1.5 rounded-full text-sm font-medium transition-colors border",
                isSelected
                  ? "bg-gray-100 border-gray-200 text-gray-900"
                  : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
              )}
            >
              {chip.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
